import {
  type ApercuReponse,
  COULEURS_CADRE_MAX,
  COULEURS_CADRE_MIN,
  HAUTEUR_CADRE_MAX_MM,
  type PlaqueApercu,
  type ReglagesCadre,
  type Reportee,
} from "./contract";
import { nombreFr, nomDepuisFichier, taille, virgule } from "./silhouettes-textes";

/**
 * Les textes du module Cadres, en fonctions pures (maquettes `Cadres*.dc.html`, plan #167) :
 * ce que la page ecrit a partir de l'image, de l'apercu et des messages de l'outil. Les nombres
 * sont a la francaise, par les fonctions des silhouettes (`nombreFr`, `virgule`), reprises et non
 * recopiees.
 *
 * L'outil ecrit en ASCII, sans accents. Ses avertissements connus (le detail detache, les
 * parties fines, le cadre ramene par le plateau) sont recrits comme la maquette
 * `CadresEtats.dc.html` les montre, avec les nombres du message ; deux autres (l'image en
 * portrait, plus de 12 plaques) sont recomposes a partir des chiffres de l'apercu, plus surs
 * qu'une phrase a decouper. Un message inconnu passe tel quel, avec sa majuscule et ses virgules
 * decimales : rien de l'outil n'est jamais perdu.
 */

/**
 * Une part de la fenetre : une decimale au plus (« 15,6 % »), deux sous 1 % pour qu'un petit
 * detail ne s'affiche pas « 0 % » (« 0,03 % »).
 */
export function pourcent(valeur: number): string {
  return `${nombreFr(valeur, valeur < 1 ? 2 : 1)} %`;
}

/** « a », « a et b », « a, b et c ». */
export function enumerer(mots: readonly (string | number)[]): string {
  if (mots.length <= 1) return mots.join("");
  return `${mots.slice(0, -1).join(", ")} et ${mots.at(-1)}`;
}

/**
 * Sous une couleur de la liste d'ordre, ce que l'ordre a reporte au fond : la part de la
 * fenetre, ce qui l'entoure, la plaque de seconde passe qui la porte, et celle qui porte le
 * reste de la couleur (maquette `CadresEtats.dc.html`, « Ton ordre ajoute une plaque », sans
 * les mots de la scene : la page ne sait pas que la creme est une lune).
 */
export function noteReportee(reportee: Reportee, plaques: readonly PlaqueApercu[]): string {
  const autour = reportee.entouree_par.length > 0 ? ` par ${enumerer(reportee.entouree_par)}` : "";
  const note = `Une partie (${pourcent(reportee.surface_pct)} de la fenêtre) est entourée${autour} : elle passe au fond, plaque ${reportee.plaque}.`;
  const reste = plaques.find(
    (plaque) => plaque.couleur === reportee.couleur && !plaque.seconde_passe,
  );
  return reste ? `${note} La plaque ${reste.rang} porte le reste.` : note;
}

/**
 * Au-dela, les parties reportees d'une couleur, ou les details detaches des avertissements, se
 * regroupent en une ligne : une image en anneaux concentriques en donnait seize, soit seize
 * encadres jaunes sous deux couleurs, et autant dans les avertissements (relecture de #176).
 */
export const DETAILLES_MAX = 2;

/**
 * Sous une couleur que Nicolas a placee devant ce qui l'entoure tout entiere : elle n'avait
 * aucune zone libre a ce rang, l'outil la garde derriere, a son rang effectif et sans plaque de
 * plus (spec, section 2, point 5), et la liste le dit, avec ce qui l'entoure quand la page le
 * sait. Depuis la tete b311690 de la partie A, `reportees` compte cette couleur entiere avec
 * son `entouree_par` ; avant, la page ne le voyait qu'a l'ordre effectif, sans savoir par quoi.
 */
export function toutEntoure(entoureePar: readonly string[] = []): string {
  const par = entoureePar.length > 0 ? ` par ${enumerer(entoureePar)}` : "";
  return `Tout ce qu'elle a est entouré${par} : elle ne peut pas avoir de plaque plus devant.`;
}

/**
 * Vrai pour une couleur entiere gardee derriere : sa plaque n'est pas une seconde passe (la
 * reponse ne porte pas `seconde_passe` sur une reportee, sa plaque le porte). Une partie
 * reportee, elle, ajoute une plaque de seconde passe.
 */
export function estEntiere(reportee: Reportee, plaques: readonly PlaqueApercu[]): boolean {
  const sienne = plaques.find((plaque) => plaque.rang === reportee.plaque);
  return sienne !== undefined && !sienne.seconde_passe;
}

/**
 * Les notes sous une couleur de la liste d'ordre : ce qui entoure la couleur si elle est gardee
 * derriere tout entiere, puis une note par partie reportee, deux au plus, puis le compte des
 * autres et leurs plaques. Une couleur entiere n'est jamais dite « une partie » : sa plaque est
 * aussi celle qui « porte le reste », et la note se contredisait (relecture de #176).
 */
export function notesReportees(
  reportees: readonly Reportee[],
  plaques: readonly PlaqueApercu[],
): string[] {
  const entieres = reportees.filter((reportee) => estEntiere(reportee, plaques));
  const parties = reportees.filter((reportee) => !estEntiere(reportee, plaques));
  const notes = [
    ...entieres.map((reportee) => toutEntoure(reportee.entouree_par)),
    ...parties.slice(0, DETAILLES_MAX).map((reportee) => noteReportee(reportee, plaques)),
  ];
  const autres = parties.slice(DETAILLES_MAX);
  if (autres.length === 0) return notes;
  const rangs = autres.map((reportee) => reportee.plaque);
  const quoi =
    autres.length > 1
      ? `Et ${autres.length} autres parties entourées, au fond : plaques ${enumerer(rangs)}.`
      : `Et 1 autre partie entourée, au fond : plaque ${rangs[0]}.`;
  return [...notes, quoi];
}

/** Le nom du cadre, tire du nom de l'image comme pour les silhouettes ; `cadre` s'il ne reste rien. */
export function nomDuCadre(nomFichier: string): string {
  return nomDepuisFichier(nomFichier, "cadre");
}

/**
 * La ligne sous le nom de l'image : `1 000 x 1 000 px · 170 Ko`, puis ses couleurs en aplats,
 * une fois l'apercu fait. (La maquette dit « opaque, en aplats » : la page ne lit pas les pixels
 * d'une petite image, elle ne sait donc pas si elle est opaque, mais elle sait ses couleurs.)
 */
export function decrireImage(
  largeur: number,
  hauteur: number,
  octets: number,
  couleurs?: number,
): string {
  const base = `${nombreFr(largeur)} x ${nombreFr(hauteur)} px · ${taille(octets)}`;
  return couleurs === undefined ? base : `${base} · ${couleurs} couleurs en aplats`;
}

/** L'aide du nom : les fichiers qu'il donnera (maquette : « donne nuit.3mf, nuit-stl.zip »). */
export function aideNom(nom: string, valide: boolean): string {
  return valide ? `donne ${nom}.3mf, ${nom}-stl.zip` : "les fichiers porteront ce nom";
}

/**
 * L'aide de la largeur : la hauteur des plaques, qui suit l'image (H = (L - 2 bordure) x
 * hauteur / largeur de l'image + 2 bordure), ou la largeur ramenee quand elle depasserait
 * 230 mm, comme `products` le fera. `ratio` : hauteur sur largeur de l'image ; `null` avant
 * la premiere image.
 */
export function aideLargeur(largeur: number, ratio: number | null, bordure: number): string {
  if (ratio === null || !(ratio > 0)) return "la hauteur suit l'image ; 230 mm au plus";
  const hauteur = (largeur - 2 * bordure) * ratio + 2 * bordure;
  if (hauteur <= HAUTEUR_CADRE_MAX_MM + 1e-6) {
    return `hauteur ${nombreFr(hauteur)} mm, qui suit l'image ; 230 mm au plus`;
  }
  const ramenee = (HAUTEUR_CADRE_MAX_MM - 2 * bordure) / ratio + 2 * bordure;
  return `hauteur 230 mm au plus : largeur ramenée à ${nombreFr(ramenee)} mm`;
}

/** L'aide des couleurs : combien l'outil en a trouve, en automatique. */
export function aideCouleurs(
  couleurs: ReglagesCadre["couleurs"],
  apercu: ApercuReponse | null,
): string {
  const bornes = `de ${COULEURS_CADRE_MIN} à ${COULEURS_CADRE_MAX}`;
  if (couleurs === "auto" && apercu)
    return `auto (${apercu.palette.length} trouvées), ou ${bornes}`;
  return `auto, ou ${bornes}`;
}

/** La note de la carte Fichiers : ce que le 3MF contient, plateau par plateau. */
export function noteFichiers(plaques: number, led = false): string {
  const base = `Un plateau par pièce : ${plaques} plaques, puis le cadre, puis le couvercle, puis le dos. Chaque pièce est nommée et sur son filament ; le cadre, le couvercle et le dos prennent celui de la première plaque`;
  // Avec la LED, le dos est blanc : la lumiere passe par lui.
  return led ? `${base} (le dos est blanc avec la LED).` : `${base}.`;
}

/**
 * Le poids annonce, a la dizaine : les plaques, plus le cadre, le couvercle et le dos quand
 * l'outil les donne (`complet`). L'apercu ne fabrique aucun maillage, et peut donc ne pas les
 * connaitre.
 */
export function grammesTotaux(apercu: ApercuReponse): { grammes: number; complet: boolean } {
  const plaques = apercu.plaques.reduce((somme, plaque) => somme + plaque.grammes, 0);
  const { grammes_cadre: cadre, grammes_dos: dos, grammes_couvercle: couvercle } = apercu.rapport;
  const complet = typeof cadre === "number" && typeof dos === "number";
  // Un products d'avant le couvercle ne le donne pas : il ne compte alors pour rien.
  const total = complet ? plaques + cadre + dos + (couvercle ?? 0) : plaques;
  return { grammes: total < 10 ? Math.round(total) : Math.round(total / 10) * 10, complet };
}

/** « environ 420 g de PLA », ou « ... pour les plaques » quand le cadre et le dos manquent. */
function poids(apercu: ApercuReponse): { long: string; court: string } {
  const { grammes, complet } = grammesTotaux(apercu);
  const nombre = nombreFr(grammes);
  return complet
    ? { long: `environ ${nombre} g de PLA`, court: `env. ${nombre} g` }
    : { long: `environ ${nombre} g de PLA pour les plaques`, court: `env. ${nombre} g, plaques` };
}

/** Le compte des parties fines : celui du rapport, sinon le plus grand d'une plaque. */
function partiesFines(apercu: ApercuReponse): number {
  if (typeof apercu.rapport.parties_fines === "number") return apercu.rapport.parties_fines;
  return Math.max(0, ...apercu.plaques.map((plaque) => plaque.parties_fines));
}

export type Puce = { long: string; court: string | null };

/**
 * Les chiffres sous l'apercu (maquette `Cadres.dc.html`), et leur version courte du telephone
 * (`CadresMobile.dc.html` : « 7 plaques », « 211 x 210 x 46,4 mm », « env. 420 g ») ; `court`
 * vaut `null` pour une puce que le telephone ne montre pas.
 */
export function pucesApercu(apercu: ApercuReponse): Puce[] {
  const n = apercu.plaques.length;
  const [largeur, hauteur] = apercu.dimensions.plaques_mm;
  const [horsL, horsH, profondeur] = apercu.dimensions.hors_tout_mm;
  const fines = partiesFines(apercu);
  const plaques = `${n} plaque${n > 1 ? "s" : ""}`;
  return [
    { long: plaques, court: plaques },
    { long: `plaques ${nombreFr(largeur, 1)} x ${nombreFr(hauteur, 1)} mm`, court: null },
    {
      long: `${nombreFr(horsL, 1)} x ${nombreFr(horsH, 1)} x ${nombreFr(profondeur, 1)} mm hors tout`,
      court: `${nombreFr(horsL, 1)} x ${nombreFr(horsH, 1)} x ${nombreFr(profondeur, 1)} mm`,
    },
    poids(apercu),
    {
      long:
        fines === 0
          ? "aucune partie fine"
          : `${fines} partie${fines > 1 ? "s" : ""} fine${fines > 1 ? "s" : ""}`,
      court: null,
    },
  ];
}

/** Un message de l'outil en clair : majuscule initiale, virgules decimales. */
export function enClair(message: string): string {
  const texte = virgule(message.trim());
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

export type Avertissement = { ton: "jaune" | "grise"; texte: string };

/*
 * Les messages de `chaine.py` que la page reconnait, tels que les vraies reponses de la partie A
 * les portent (relecture de #176, PR #178). Ils sont en ASCII, et leurs nombres a point.
 */

/** Le detail detache : couleur, part de la fenetre, ce qui l'entoure. */
const DETAIL_DETACHE =
  /tache (\S+) de ([\d.]+) % de la fenetre est seule, entouree par (.+?) : elle passe au fond/;

/**
 * Les parties fines : « 11 partie(s) fine(s), moins de 0.8 mm de matiere ou 0.6 mm de creux :
 * gardees telles quelles, montrees en rose sur la planche. » La chaine ne distingue pas la
 * matiere du creux : un seul compte pour les deux.
 */
const PARTIES_FINES =
  /^(\d+) partie\(s\) fine\(s\)(?:, moins de ([\d.]+) mm de matiere ou ([\d.]+) mm de creux)?.*montrees en rose/;

/** « 5 couleurs demandees, 4 trouvees : les autres etaient trop proches... » */
const COULEURS_PROCHES = /^(\d+) couleurs demandees, (\d+) trouvees/;

/**
 * Le fond de la LED trop sombre : « eclairage LED : le fond (noir) est fonce, il s'allumera a
 * peine. Mettre une couleur claire sur la derniere plaque. » (`chaine.py`, cadre a rainures).
 */
const FOND_FONCE = /^eclairage LED : le fond \((.+)\) est fonce, il s'allumera a peine/;

/**
 * La largeur ramenee, et sa raison. L'image en portrait (« image en portrait : largeur ramenee a
 * 127 mm pour tenir dans 230 mm de haut ») : la page recompose le message de ses chiffres. Ou,
 * depuis la tete b311690 de la partie A, le cadre qui ne tiendrait pas sur le plateau de la P1S
 * (« largeur ramenee a 220.9 mm : au-dela, le cadre ne tient pas sur le plateau de la P1S (zone
 * exclue de 18 x 28 mm dans un coin) ») : c'est ce message-la qui sort quand la hauteur et le
 * plateau ramenent tous deux la largeur. Tout autre message de largeur ramenee passe tel quel,
 * sans raison pretee : un seul motif pour les deux recrivait le plateau en « portrait ».
 */
const PORTRAIT = /^image en portrait : largeur ramenee/;
const PLATEAU =
  /^largeur ramenee a ([\d.]+) mm : au-dela, le cadre ne tient pas sur le plateau de la P1S(?: \(zone exclue de ([\d.]+) x ([\d.]+) mm)?/;
const LARGEUR_RAMENEE = /largeur ramenee/;

/** Le message que la page recompose de ses chiffres, et dont elle tait la version brute. */
const TROP_DE_PLAQUES = /\d+ plaques.*moins de couleurs/;

/** Au-dela, l'outil previent (`PLAQUES_ALERTE` de `chaine.py`). */
const PLAQUES_ALERTE = 12;

/** « L'image est en portrait » : la largeur ramenee, et ce qu'aurait donne la largeur demandee. */
function portrait(apercu: ApercuReponse, demandee: number | undefined): Avertissement {
  const [largeur] = apercu.dimensions.plaques_mm;
  const [fenetreL, fenetreH] = apercu.dimensions.fenetre_mm;
  const fin = `Largeur ramenée à ${nombreFr(largeur)} mm, pour tenir dans les ${HAUTEUR_CADRE_MAX_MM} mm du plateau.`;
  if (demandee === undefined || !(fenetreL > 0)) {
    return { ton: "grise", texte: `L'image est en portrait. ${fin}` };
  }
  const bordure = (largeur - fenetreL) / 2;
  const auraitFait = (demandee - 2 * bordure) * (fenetreH / fenetreL) + 2 * bordure;
  return {
    ton: "grise",
    texte: `L'image est en portrait : à ${nombreFr(demandee)} mm de large, les plaques feraient ${nombreFr(auraitFait)} mm de haut. ${fin}`,
  };
}

/** Le cadre ramene pour tenir sur le plateau de la P1S, hors du coin que l'imprimante reserve. */
function plateauEnClair(trouve: RegExpExecArray): Avertissement {
  const coin =
    trouve[2] && trouve[3]
      ? `, dont un coin de ${virgule(trouve[2])} x ${virgule(trouve[3])} mm est réservé`
      : "";
  return {
    ton: "grise",
    texte: `Largeur ramenée à ${nombreFr(Number(trouve[1]), 1)} mm : au-delà, le cadre ne tient pas sur le plateau de la P1S${coin}.`,
  };
}

/** Plus de 12 plaques : le poids et le nombre de plateaux, cadre, couvercle et dos compris. */
function tropDePlaques(apercu: ApercuReponse): Avertissement {
  const n = apercu.plaques.length;
  const { grammes, complet } = grammesTotaux(apercu);
  const pla = `environ ${nombreFr(grammes)} g de PLA${complet ? "" : " pour les plaques"}`;
  return {
    ton: "jaune",
    texte: `${n} plaques : ${pla} et ${n + 3} plateaux avec le cadre, le couvercle et le dos. Une photo ou un dégradé fin donnent beaucoup de zones : impose moins de couleurs.`,
  };
}

/** Les parties fines, au singulier comme au pluriel. */
function partiesFinesEnClair(trouve: RegExpExecArray): string {
  const nombre = Number(trouve[1]);
  const plusieurs = nombre > 1;
  const seuils =
    trouve[2] && trouve[3]
      ? ` (moins de ${virgule(trouve[2])} mm de matière, ou moins de ${virgule(trouve[3])} mm de creux)`
      : "";
  const quoi = plusieurs ? `${nombre} parties fines` : `${nombre} partie fine`;
  const gardees = plusieurs
    ? "gardées telles quelles, montrées en rose sur la planche PNG. Elles risquent"
    : "gardée telle quelle, montrée en rose sur la planche PNG. Elle risque";
  return `${quoi}${seuils} : ${gardees} de casser au décollement, ou de se boucher.`;
}

/**
 * Les avertissements de l'apercu, dans l'ordre de l'outil et les mots de la maquette
 * `CadresEtats.dc.html`. Deux sont recomposes a partir des chiffres de l'apercu, plus surs
 * qu'une phrase a decouper : l'image en portrait (en gris, comme le plateau : ce n'est pas un
 * defaut de l'image) et plus de 12 plaques. Les details detaches se regroupent au-dela de deux.
 *
 * La largeur ramenee se lit dans le rapport (`largeur_reduite`, `largeur_demandee_mm`), jamais
 * dans la largeur tapee : pendant les 600 ms et le calcul, celle-ci en demande deja une autre,
 * et la page affichait un faux « portrait » (relecture de #176). `largeurDemandee`, la largeur
 * de la requete de l'apercu affiche, ne sert qu'a un rapport qui n'aurait pas ces deux champs.
 * Sa raison se lit dans le message : portrait ou plateau, chacun dit a sa place, aucun perdu.
 */
export function avertissements(apercu: ApercuReponse, largeurDemandee?: number): Avertissement[] {
  const { rapport } = apercu;
  const [largeur] = apercu.dimensions.plaques_mm;
  const demandee = rapport.largeur_demandee_mm ?? largeurDemandee;
  const reduite = rapport.largeur_reduite ?? (demandee !== undefined && largeur < demandee - 0.05);
  const trop = apercu.plaques.length > PLAQUES_ALERTE;
  const detaches = rapport.avertissements.filter((message) => DETAIL_DETACHE.test(message));

  const resultat: Avertissement[] = [];
  let portraitDit = false;
  let largeurDite = false;
  let tropDit = false;
  let detachesVus = 0;
  for (const message of rapport.avertissements) {
    const plateau = PLATEAU.exec(message);
    if (plateau) {
      resultat.push(plateauEnClair(plateau));
      largeurDite = true;
      continue;
    }
    if (PORTRAIT.test(message)) {
      if (!portraitDit)
        resultat.push(
          reduite ? portrait(apercu, demandee) : { ton: "grise", texte: enClair(message) },
        );
      portraitDit = true;
      largeurDite = true;
      continue;
    }
    if (LARGEUR_RAMENEE.test(message)) {
      resultat.push({ ton: "grise", texte: enClair(message) });
      largeurDite = true;
      continue;
    }
    if (TROP_DE_PLAQUES.test(message)) {
      if (!tropDit && trop) resultat.push(tropDePlaques(apercu));
      tropDit = true;
      continue;
    }
    const detache = DETAIL_DETACHE.exec(message);
    if (detache) {
      detachesVus += 1;
      if (detachesVus <= DETAILLES_MAX) {
        const [, couleur, part, autour] = detache;
        // Seul, le detail coute une plaque que l'on peut dire ; a plusieurs, le compte melangerait.
        const cout =
          detaches.length === 1
            ? ` (${apercu.plaques_ordre_propose} au lieu de ${apercu.plaques_ordre_propose - 1})`
            : "";
        resultat.push({
          ton: "jaune",
          texte: `Une tache de ${couleur} (${pourcent(Number(part))} de la fenêtre) est seule, entourée par ${autour} : elle passe au fond et ajoute une plaque${cout}. Relie-la au reste dans l'image, ou efface-la.`,
        });
      } else if (detachesVus === DETAILLES_MAX + 1) {
        const autres = detaches.length - DETAILLES_MAX;
        resultat.push({
          ton: "jaune",
          texte:
            autres > 1
              ? `Et ${autres} autres détails détachés : chacun passe au fond et ajoute une plaque.`
              : "Et 1 autre détail détaché : il passe au fond et ajoute une plaque.",
        });
      }
      continue;
    }
    const fines = PARTIES_FINES.exec(message);
    if (fines) {
      resultat.push({ ton: "jaune", texte: partiesFinesEnClair(fines) });
      continue;
    }
    const fonce = FOND_FONCE.exec(message);
    if (fonce) {
      resultat.push({
        ton: "jaune",
        texte: `Éclairage LED : le fond (${fonce[1]}) est foncé, il s'allumera à peine. Mets une couleur claire sur la dernière plaque.`,
      });
      continue;
    }
    const proches = COULEURS_PROCHES.exec(message);
    if (proches) {
      resultat.push({
        ton: "grise",
        texte: `${proches[1]} couleurs demandées, ${proches[2]} trouvées : les autres étaient trop proches pour faire une plaque de plus.`,
      });
      continue;
    }
    resultat.push({ ton: "jaune", texte: enClair(message) });
  }
  // Ce que les chiffres disent sans que l'outil l'ait ecrit (un products plus ancien, qui ne
  // ramenait la largeur que pour la hauteur) : a la fin.
  if (reduite && !largeurDite) resultat.push(portrait(apercu, demandee));
  if (trop && !tropDit) resultat.push(tropDePlaques(apercu));
  return resultat;
}

/** A ajouter a tout refus de l'image (spec, section 5, « Image refusee »). */
export const APLATS =
  "Essaie une image en aplats de couleur : un dessin, une illustration, pas une photo.";

/** Le calcul tue a 20 s dans son processus (maquette `CadresEtats.dc.html`, « Image trop detaillee »). */
export const TROP_DETAILLEE =
  "Le calcul s'est arrêté au bout de 20 s : l'image est trop détaillée pour un cadre. Impose moins de couleurs, ou prends une image plus simple.";

/** Vrai pour le refus du processus isole de `products`, tue au bout de 20 s. */
export function estInterrompu(message: string): boolean {
  return /interrompu/.test(message);
}

/** Vrai quand l'ordre envoye ne nomme plus la palette : la page repart de la proposition. */
export function estOrdreInvalide(message: string): boolean {
  return /^ordre invalide/.test(message.trim());
}

/**
 * Les refus connus de la chaine : leur texte, et s'ils viennent de l'image. Le conseil des
 * aplats suit un refus de l'image (spec, section 5), pas un cadre trop grand pour la P1S : les
 * deux refus du plateau (tete b311690) ne disent rien de l'image, et s'y tromper enverrait
 * chercher un dessin quand il faut une largeur plus petite.
 */
type Refus = { motif: RegExp; texte: (trouve: RegExpExecArray) => string; image: boolean };

const REFUS: readonly Refus[] = [
  {
    motif: /^une seule couleur trouvee/,
    texte: () => "Une seule couleur trouvée : un cadre en demande au moins deux.",
    image: true,
  },
  {
    // « de large ou de haut » depuis la tete b311690 (une bande tres large), « de large » avant.
    motif: /^image trop etiree(?: : la fenetre ferait moins de ([\d.]+) mm de ([a-z ]+))?/,
    texte: (trouve) =>
      trouve[1] && trouve[2]
        ? `L'image est trop étirée : la fenêtre ferait moins de ${virgule(trouve[1])} mm de ${trouve[2].trim()}.`
        : "L'image est trop étirée pour un cadre.",
    image: true,
  },
  {
    // Au placement des pieces du 3MF : le numero du plateau, ses pieces, sa taille.
    motif: /^plateau (\d+) \((.+)\) : ([\d.]+) x ([\d.]+) mm, trop grand pour le plateau de la P1S/,
    texte: (trouve) =>
      `Le plateau ${trouve[1]} du 3MF (${trouve[2]}) mesure ${virgule(trouve[3] ?? "")} x ${virgule(trouve[4] ?? "")} mm : trop grand pour la P1S, hors du coin qu'elle réserve. Réduis la largeur, l'écart entre les plaques ou le nombre de couleurs.`,
    image: false,
  },
  {
    motif: /^le cadre ne tient pas sur le plateau de la P1S, meme a ([\d.]+) mm de large/,
    texte: (trouve) =>
      `Le cadre ne tient pas sur le plateau de la P1S, même à ${virgule(trouve[1] ?? "")} mm de large. Recadre l'image.`,
    image: false,
  },
];

/**
 * Un refus de l'outil (400), dans son texte a lui quand il est connu, en clair sinon ; suivi du
 * conseil des aplats, sauf pour un cadre trop grand pour la P1S.
 */
export function refusEnClair(message: string): string {
  const net = message.trim();
  for (const refus of REFUS) {
    const trouve = refus.motif.exec(net);
    if (trouve) return refus.image ? `${refus.texte(trouve)} ${APLATS}` : refus.texte(trouve);
  }
  return `${enClair(message)} ${APLATS}`;
}
